import { useMemo } from 'react';
import styled from '@emotion/styled';

import { Button, Divider, Icon, Link, Modal, Stack, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import churnDeflectionImg from '@hotjar/shared-assets/images/payments/churn-deflection.svg';
import { trackClick } from '@hotjar/webapp/tracking/common';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import type { OwnedAccount } from '@hotjar/account';
import { mapOwnedAccountModelToDto, useOwnedAccount } from '@hotjar/account';
import { usePricingTableByAccount } from '@hotjar/webapp/hooks/usePricingTable';
import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { BASIC } from '@hotjar/webapp/domains/revenue/plan/feature-group';

import { MODAL_NAME } from '../types';

const S = {
  ModalBody: styled(Modal.Body)`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x3 })}
    padding-top: ${tokens.spacer.x8};
  `,
  TextNoMargin: styled(Text)`
    margin: 0;
  `,
  Content: styled(Stack)`
    flex: 1;
  `,
  FeaturesList: styled.ul`
    ${tokens.flexContainer('form', { gap: tokens.spacer.x2 })}
    list-style: none;
    margin: 0;
    padding: 0;
  `,
  FeatureItem: styled.li`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x1 })}
    align-items: center;
  `,
  Divider: styled(Divider)`
    margin-top: auto;
  `,
  CtaContainer: styled(Stack)`
    justify-content: space-between;
  `,
};

interface Props {
  onSubmit(): void;
  onCancel(): void;
  highestPlan?: FeatureGroup;
}

const ACTIONS = [
  { key: 'compare-plans', url: 'https://www.hotjar.com/pricing/' },
  { key: 'get-started', url: 'https://www.hotjar.com/customers/' },
] as const;

export function ChurnDeflection({
  onSubmit,
  onCancel,
  highestPlan = 'business',
}: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('billing:churn-deflection');

  const { data: account } = useOwnedAccount();
  const { priceOrderedPlans } = usePricingTableByAccount(
    mapOwnedAccountModelToDto(account as OwnedAccount),
  );
  const planRepository = usePlanRepository();

  const defaultBasicPlan = useMemo(
    () =>
      priceOrderedPlans
        .map(([planId]) => planRepository.byId(planId))
        .find((plan) => plan.featureGroup === BASIC),
    [planRepository, priceOrderedPlans],
  );

  return (
    <>
      <S.ModalBody>
        <img src={churnDeflectionImg} alt={t('title')} />
        <S.Content direction="column" spacing="x3">
          <Text as="h2" textType="header4">
            {t('title')}
          </Text>
          <S.TextNoMargin as="p" textType="subtitle1">
            {t('subtitle')}
          </S.TextNoMargin>
          <S.FeaturesList>
            {t<string, string[]>(`features.${highestPlan}`, {
              returnObjects: true,
              ccDailyAllowance: defaultBasicPlan?.ccDailyAllowance,
            }).map((text) => (
              <S.FeatureItem key={text}>
                <Icon icon="close" color="text_warning" />
                <Text as="span" textType="body2" textColor="text_light">
                  {text}
                </Text>
              </S.FeatureItem>
            ))}
          </S.FeaturesList>
          <S.Divider />
          <S.CtaContainer>
            {ACTIONS.map(({ key, url }) => (
              <Stack direction="column" spacing="x2" key={key}>
                <S.TextNoMargin as="p" textType="subtitle2">
                  {t(`${key}.title`)}
                </S.TextNoMargin>
                <S.TextNoMargin as="p" textType="body2" textColor="text_light">
                  {t(`${key}.description`)}
                </S.TextNoMargin>
                <Link
                  href={url}
                  onClick={() => {
                    trackClick({
                      name: t(`${key}.action`),
                      uiPosition: MODAL_NAME.churnDeflection,
                      groupData: extractAccountGroupingProps(account),
                    });
                  }}
                  kind="standalone"
                  isExternal
                  title={t(`${key}.title`)}
                >
                  {t(`${key}.action`)}
                </Link>
              </Stack>
            ))}
          </S.CtaContainer>
        </S.Content>
      </S.ModalBody>
      <Modal.Footer>
        <Button kind="ghost" onClick={onSubmit}>
          {t('action.continue')}
        </Button>
        <Button onClick={onCancel}>{t('action.cancel')}</Button>
      </Modal.Footer>
    </>
  );
}
